/**
 * Peak vs Plateau Mock
 *
 * Shows two sensitivity curves side by side: a sharp peak
 * (overfitted) and a broad plateau (robust). Illustrates
 * the core concept of parameter stability.
 */

import { AreaChart, Area, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// Overfitted: sharp peak at EMA(17)
const peakData = [
  { x: 12, y: 0.3 }, { x: 13, y: 0.4 }, { x: 14, y: 0.5 },
  { x: 15, y: 0.7 }, { x: 16, y: 1.1 }, { x: 17, y: 1.8 },
  { x: 18, y: 1.0 }, { x: 19, y: 0.6 }, { x: 20, y: 0.4 },
  { x: 21, y: 0.3 }, { x: 22, y: 0.2 }, { x: 23, y: 0.15 },
];

// Robust: broad plateau from EMA(15)-EMA(22)
const plateauData = [
  { x: 12, y: 0.6 }, { x: 13, y: 0.8 }, { x: 14, y: 1.0 },
  { x: 15, y: 1.2 }, { x: 16, y: 1.25 }, { x: 17, y: 1.3 },
  { x: 18, y: 1.35 }, { x: 19, y: 1.3 }, { x: 20, y: 1.25 },
  { x: 21, y: 1.2 }, { x: 22, y: 1.15 }, { x: 23, y: 0.9 },
];

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

function Chart({ data, label, color, verdict, verdictColor }: {
  data: { x: number; y: number }[];
  label: string;
  color: string;
  verdict: string;
  verdictColor: string;
}) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '0.5625rem', color: '#94a3b8', marginBottom: '0.2rem', textAlign: 'center' }}>
        {label}
      </div>
      <ResponsiveContainer width="100%" height={80}>
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <YAxis hide domain={[0, 2]} />
          <Tooltip
            contentStyle={tipStyle}
            formatter={(v: number) => [v.toFixed(2), 'Sharpe']}
            labelFormatter={(l) => `EMA ${l}`}
            cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
          />
          <Area
            type="monotone"
            dataKey="y"
            stroke={color}
            fill={color}
            fillOpacity={0.08}
            strokeWidth={1.5}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
      <div style={{ textAlign: 'center', fontSize: '0.5rem', fontWeight: 700, color: verdictColor, marginTop: '0.1rem' }}>
        {verdict}
      </div>
    </div>
  );
}

export default function PeakVsPlateauMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Peak vs. Plateau &mdash; EMA Period Sensitivity
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Chart data={peakData} label="Overfitted (Peak)" color="#ef4444" verdict="Fragile" verdictColor="#ef4444" />
          <Chart data={plateauData} label="Robust (Plateau)" color="#22c55e" verdict="Stable" verdictColor="#22c55e" />
        </div>
        <div style={{ textAlign: 'center', fontSize: '0.5rem', color: '#64748b', marginTop: '0.375rem' }}>
          X-axis: EMA Period &mdash; Y-axis: Sharpe Ratio
        </div>
      </div>
      <div className="platform-mock-caption">
        The peak collapses with a 1-unit shift. The plateau holds across 8 values.
      </div>
    </div>
  );
}
